import { defineStore } from "pinia";

import { CustomerApiError } from "../api/customerAuth";
import {
  createCustomerCartIdempotencyKey,
  CustomerCart,
  CustomerCartItemInput,
  CustomerCartPreferencesInput,
  customerCartApi,
} from "../api/customerCart";

import { useCustomerSession } from "./customerSession";

export const CUSTOMER_CART_EVENT_KEY = "tirss:customer:cart-event";
const CUSTOMER_CART_CHANNEL = "tirss:customer:cart";
const REVISION_CONFLICT_MESSAGE = "Sua cesta foi atualizada em outro lugar.";

interface CustomerCartSignal {
  type: "refresh";
  id: string;
}

interface FailedMutationRequest {
  operation: string;
  idempotencyKey: string;
  request: (meta: { revision: number; idempotencyKey: string }) => Promise<CustomerCart>;
  intent: CustomerCartMutationIntent;
  expectedRevision?: number;
}

interface CustomerCartMutationIntent {
  customerId: string;
  sessionGeneration: number;
  cartGeneration: number;
}

let cartChannel: BroadcastChannel | null = null;
let lastSignalId = "";
const listenerDisposers = new WeakMap<object, () => void>();
let mutationTail: Promise<void> = Promise.resolve();
let failedMutationRequest: FailedMutationRequest | null = null;

function storage() {
  if (typeof window === "undefined") return null;

  return window.localStorage;
}

function channel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!cartChannel) cartChannel = new BroadcastChannel(CUSTOMER_CART_CHANNEL);

  return cartChannel;
}

function signalId() {
  return createCustomerCartIdempotencyKey();
}

function publishRefresh() {
  const message: CustomerCartSignal = { type: "refresh", id: signalId() };
  channel()?.postMessage(message);
  try {
    storage()?.setItem(CUSTOMER_CART_EVENT_KEY, JSON.stringify(message));
  } catch {
    // A signal is best-effort; the next focus/online event restores the projection.
  }
}

export const useCustomerCart = defineStore("customerCart", {
  state: () => ({
    cart: null as CustomerCart | null,
    initialized: false,
    loading: false,
    stale: false,
    error: "",
    generation: 0,
    intentGeneration: 0,
    mutationRevision: 0,
    pendingAction: "" as string,
    failedMutation: null as { operation: string; idempotencyKey: string } | null,
  }),
  getters: {
    totalUnits: (state) => state.cart?.totals.totalUnits ?? 0,
  },
  actions: {
    start() {
      if (typeof window === "undefined") return;
      const store = this as unknown as object;
      if (listenerDisposers.has(store)) return;

      const onFocus = () => void this.restore(true);
      const onOnline = () => void this.restore(true);
      const onStorage = (event: StorageEvent) => {
        if (event.key !== CUSTOMER_CART_EVENT_KEY || !event.newValue) return;
        this.handleSignal(event.newValue);
      };
      const onMessage = (event: MessageEvent<CustomerCartSignal>) => {
        if (event.data) this.handleSignal(event.data);
      };

      window.addEventListener("focus", onFocus);
      window.addEventListener("online", onOnline);
      window.addEventListener("storage", onStorage);
      channel()?.addEventListener("message", onMessage);

      listenerDisposers.set(store, () => {
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("online", onOnline);
        window.removeEventListener("storage", onStorage);
        channel()?.removeEventListener("message", onMessage);
      });
      const session = useCustomerSession();
      if (session.user) void this.restore();
    },

    handleSignal(raw: CustomerCartSignal | string) {
      let candidate: unknown = raw;
      try {
        candidate = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch {
        return;
      }
      if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return;
      const message = candidate as Partial<CustomerCartSignal>;
      if (
        message.type !== "refresh" ||
        typeof message.id !== "string" ||
        !message.id ||
        message.id.length > 128 ||
        message.id === lastSignalId
      )
        return;
      lastSignalId = message.id;
      void this.restore(true);
    },

    async restore(force = false): Promise<void> {
      const session = useCustomerSession();
      if (!session.user) {
        this.clear();

        return;
      }
      if (!force && (this.initialized || this.loading)) return;
      const generation = ++this.generation;
      this.loading = true;
      this.error = "";
      try {
        const next = await session.authenticated(() => customerCartApi.getCurrent());
        if (generation !== this.generation || !session.user) return;
        this.cart = next;
        this.mutationRevision = next.revision;
        this.initialized = true;
        this.stale = false;
      } catch (error) {
        if (generation !== this.generation) return;
        this.stale = Boolean(this.cart);
        this.error =
          error instanceof Error ? error.message : "Não foi possível carregar sua cesta.";
        if (error instanceof CustomerApiError && error.status === 401) this.clear();
      } finally {
        if (generation === this.generation) this.loading = false;
      }
    },

    clear() {
      this.generation++;
      this.intentGeneration++;
      this.mutationRevision = 0;
      this.cart = null;
      this.initialized = false;
      this.loading = false;
      this.stale = false;
      this.error = "";
      this.pendingAction = "";
      this.failedMutation = null;
      failedMutationRequest = null;
    },

    dispose() {
      this.generation++;
      this.intentGeneration++;
      this.loading = false;
      this.pendingAction = "";
      const store = this as unknown as object;
      listenerDisposers.get(store)?.();
      listenerDisposers.delete(store);
      failedMutationRequest = null;
      cartChannel?.close();
      cartChannel = null;
      lastSignalId = "";
    },

    addItem(input: CustomerCartItemInput) {
      return this.mutate("addItem", (meta) => customerCartApi.addItem(input, meta));
    },
    replaceItem(cartItemId: string, input: CustomerCartItemInput, expectedRevision?: number) {
      return this.mutate(
        "replaceItem",
        (meta) => customerCartApi.replaceItem(cartItemId, input, meta),
        expectedRevision
      );
    },
    removeItem(cartItemId: string) {
      return this.mutate("removeItem", (meta) => customerCartApi.removeItem(cartItemId, meta));
    },
    clearCart() {
      return this.mutate("clear", (meta) => customerCartApi.clear(meta));
    },
    updatePreferences(input: CustomerCartPreferencesInput) {
      return this.mutate("updatePreferences", (meta) =>
        customerCartApi.updatePreferences(input, meta)
      );
    },
    acceptPrice(cartItemId: string) {
      return this.mutate("acceptPrice", (meta) => customerCartApi.acceptPrice(cartItemId, meta));
    },

    retryFailedMutation() {
      if (!failedMutationRequest) {
        throw new CustomerApiError("Não há uma operação de cesta para tentar novamente.");
      }
      const failed = failedMutationRequest;

      return this.enqueueMutation(
        failed.operation,
        failed.request,
        failed.idempotencyKey,
        failed.intent,
        failed.expectedRevision
      );
    },

    async mutate(
      operation: string,
      request: (meta: { revision: number; idempotencyKey: string }) => Promise<CustomerCart>,
      expectedRevision?: number
    ) {
      const session = useCustomerSession();
      if (!session.user) {
        throw new CustomerApiError("Sua sessão expirou. Entre novamente.", 401);
      }
      const intentKey = createCustomerCartIdempotencyKey();
      const intent: CustomerCartMutationIntent = {
        customerId: session.user.id,
        sessionGeneration: session.generation,
        cartGeneration: this.intentGeneration,
      };

      return this.enqueueMutation(operation, request, intentKey, intent, expectedRevision);
    },

    async enqueueMutation(
      operation: string,
      request: (meta: { revision: number; idempotencyKey: string }) => Promise<CustomerCart>,
      intentKey: string,
      intent: CustomerCartMutationIntent,
      expectedRevision?: number
    ) {
      const run = async () => {
        const session = useCustomerSession();
        const intentIsCurrent = () =>
          intent.cartGeneration === this.intentGeneration &&
          intent.sessionGeneration === session.generation &&
          intent.customerId === session.user?.id;
        if (!intentIsCurrent()) {
          throw new CustomerApiError(
            "Esta operação da cesta não pertence mais à sessão atual.",
            409,
            {},
            "CART_INTENT_STALE"
          );
        }
        const meta = {
          revision: expectedRevision ?? (this.mutationRevision || this.cart?.revision || 0),
          idempotencyKey: intentKey,
        };
        this.pendingAction = operation;
        this.failedMutation = null;
        if (failedMutationRequest?.idempotencyKey !== intentKey) failedMutationRequest = null;
        try {
          const next = await session.authenticated(() => request(meta));
          if (intentIsCurrent()) {
            this.generation++;
            this.mutationRevision = next.revision;
            this.cart = next;
            this.initialized = true;
            this.loading = false;
            this.stale = false;
            this.error = "";
            failedMutationRequest = null;
            publishRefresh();
          }

          return next;
        } catch (error) {
          const revisionConflict =
            error instanceof CustomerApiError &&
            error.status === 409 &&
            error.code === "CART_REVISION_CONFLICT";
          const requestStillCurrent = intentIsCurrent();
          if (revisionConflict && requestStillCurrent) {
            await this.restore(true);
            if (intentIsCurrent()) {
              this.error = REVISION_CONFLICT_MESSAGE;
            }
          } else if (requestStillCurrent && error instanceof Error) {
            this.error = error.message;
            if (error instanceof CustomerApiError && (error.status === 0 || error.status >= 500)) {
              failedMutationRequest = {
                operation,
                idempotencyKey: intentKey,
                request,
                intent,
                expectedRevision,
              };
              this.failedMutation = { operation, idempotencyKey: intentKey };
            }
          }
          if (
            (!requestStillCurrent ||
              !(error instanceof CustomerApiError) ||
              (error.status !== 0 && error.status < 500)) &&
            failedMutationRequest?.idempotencyKey === intentKey
          ) {
            failedMutationRequest = null;
            this.failedMutation = null;
          }
          throw error;
        } finally {
          this.pendingAction = "";
        }
      };

      const next = mutationTail.then(run, run);
      mutationTail = next.then(
        () => undefined,
        () => undefined
      );

      return next;
    },
  },
});
